import PropTypes from "prop-types";
import {
	arrow,
	FloatingPortal,
	offset,
	shift,
	useClick,
	flip,
	useDismiss,
	useFloating,
	useInteractions,
} from "@floating-ui/react";
import { useContext, useRef, useState } from "react";
import "./FloatingButton.scss";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import useClickAway from "app/utils/hooks/useClickAway";

const FloatingButton = ({
	referenceComponent = false,
	floatingContent,
	position,
	onClick = () => {},
	onClickAway = () => {},
	popoverClassName,
	floatingOffsetMobile = [0, 0],
	floatingOffsetDesk = [0, 0],
	zIndexValue,
	referenceButtonClassName,
	displayCrossIcon = false,
	floatingContainerClassName,
	dismissOnFloatingClick = false,
	disableDismissOnScroll = false,
}) => {
	const [open, setOpen] = useState(false);
	const floatingContentRef = useRef();
	const arrowRef = useRef();

	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const {
		x,
		y,
		refs,
		strategy,
		placement,
		context,
		middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
	} = useFloating({
		placement: position,
		strategy: "absolute",
		open,
		onOpenChange: setOpen,
		middleware: [offset(10), shift(), flip(), arrow({ element: arrowRef })],
	});

	const dismiss = useDismiss(context, { ancestorScroll: !disableDismissOnScroll });
	const click = useClick(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

	const handleClick = () => {
		if (dismissOnFloatingClick) {
			setOpen(false);
		}
	};

	useClickAway(floatingContentRef, onClickAway);

	// Utiliser le placement actuel (qui peut avoir été modifié par flip())
	const actualPlacement = placement.split("-")[0];

	const staticSide = {
		top: "bottom",
		right: "left",
		bottom: "top",
		left: "right",
	}[actualPlacement];

	// Calcul des offsets en tenant compte du placement actuel
	let arrowPositionX = arrowX;
	let arrowPositionY = arrowY;

	if (actualPlacement === "top" || actualPlacement === "bottom") {
		arrowPositionX -= isMobile ? floatingOffsetMobile[0] : floatingOffsetDesk[0];
	} else {
		arrowPositionY += isMobile ? floatingOffsetMobile[1] : floatingOffsetDesk[1];
	}

	// Style de base pour la flèche
	const arrowStyle = {
		left: arrowX ? `${arrowPositionX}px` : "",
		top: arrowY ? `${arrowPositionY}px` : "",
		[staticSide]: "-5px", // Position la flèche du bon côté
	};

	// Applique les styles de bordure en fonction du placement actuel
	if (staticSide === "top") {
		arrowStyle.borderTop = "1px solid #e6e6e6";
		arrowStyle.borderLeft = "1px solid #e6e6e6";
		arrowStyle.borderRight = "none";
		arrowStyle.borderBottom = "none";
	} else if (staticSide === "right") {
		arrowStyle.borderTop = "1px solid #e6e6e6";
		arrowStyle.borderRight = "1px solid #e6e6e6";
		arrowStyle.borderLeft = "none";
		arrowStyle.borderBottom = "none";
	} else if (staticSide === "bottom") {
		arrowStyle.borderBottom = "1px solid #e6e6e6";
		arrowStyle.borderRight = "1px solid #e6e6e6";
		arrowStyle.borderLeft = "none";
		arrowStyle.borderTop = "none";
	} else if (staticSide === "left") {
		arrowStyle.borderBottom = "1px solid #e6e6e6";
		arrowStyle.borderLeft = "1px solid #e6e6e6";
		arrowStyle.borderRight = "none";
		arrowStyle.borderTop = "none";
	}

	return (
		<div className={`floating-button ${floatingContainerClassName ?? ""}`} onClick={onClick}>
			<span
				data-testid="floating-button-ref"
				ref={refs.setReference}
				{...getReferenceProps({})}
				className={referenceButtonClassName}
			>
				{referenceComponent}
			</span>
			<FloatingPortal>
				{open && (
					<div
						ref={refs.setFloating}
						{...getFloatingProps({
							style: {
								position: strategy,
								left:
									x +
										(isMobile
											? floatingOffsetMobile[0]
											: floatingOffsetDesk[0]) ?? "",
								top:
									y +
										(isMobile
											? floatingOffsetMobile[1]
											: floatingOffsetDesk[1]) ?? "",
								zIndex: zIndexValue ? zIndexValue : 4,
							},
						})}
						{...popoverClassName && { className: popoverClassName }}
					>
						<div
							ref={floatingContentRef}
							className="floating-button__content"
							onClick={handleClick}
						>
							{floatingContent}
							{displayCrossIcon && <i className="icon icon--cross-dark" />}
						</div>
						<div className="floating-button__arrow" ref={arrowRef} style={arrowStyle} />
					</div>
				)}
			</FloatingPortal>
		</div>
	);
};

FloatingButton.propTypes = {
	position: PropTypes.oneOf(["top", "bottom", "left", "right"]),
	referenceComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	floatingContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	onClick: PropTypes.func,
	onClickAway: PropTypes.func,
	popoverClassName: PropTypes.string,
	floatingOffsetDesk: PropTypes.array,
	floatingOffsetMobile: PropTypes.array,
	zIndexValue: PropTypes.number,
	referenceButtonClassName: PropTypes.string,
	displayCrossIcon: PropTypes.bool,
	floatingContainerClassName: PropTypes.string,
	dismissOnFloatingClick: PropTypes.bool,
	disableDismissOnScroll: PropTypes.bool,
};

export default FloatingButton;
